import { useState, useEffect } from 'react';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { parseISO, format, parse } from 'date-fns';
import Data from '../../../repository';
import Loading from '../../components/Loading';
import styles from '../../../styles/Admin.module.scss';

function Manage() {
	const [staff, setStaff] = useState([]);
	const [selectedStaff, setSelectedStaff] = useState(null);
	const [currentWeekStart, setCurrentWeekStart] = useState(moment().startOf('isoWeek'));
	const [availability, setAvailability] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadingAvailability, setLoadingAvailability] = useState(false);
	const [saving, setSaving] = useState(false);
	const [editingDay, setEditingDay] = useState(null);
	const [editWindows, setEditWindows] = useState([{ start: '09:00', end: '17:00' }]);
	const [showApplyToWeek, setShowApplyToWeek] = useState(false);
	const [excludedDays, setExcludedDays] = useState([]);
	const [repeatMode, setRepeatMode] = useState('this-week');
	const [repeatEndDate, setRepeatEndDate] = useState('');

	useEffect(() => {
		fetchStaff();
	}, []);

	useEffect(() => {
		if (selectedStaff) {
			fetchAvailability();
		}
	}, [selectedStaff, currentWeekStart]);

	const fetchStaff = async () => {
		try {
			const res = await Data.fetchStaff();
			const activeStaff = res.data.filter(s => s.enabled && !s.isDeleted);
			setStaff(activeStaff);
			if (activeStaff.length > 0) {
				setSelectedStaff(activeStaff[0]);
			}
		} catch (error) {
			console.error('Error fetching staff:', error);
		} finally {
			setLoading(false);
		}
	};

	const fetchAvailability = async () => {
		if (!selectedStaff) return;

		setLoadingAvailability(true);
		try {
			const start = currentWeekStart.format('YYYY-MM-DD');
			const end = moment(currentWeekStart).add(6, 'days').format('YYYY-MM-DD');
			const res = await Data.fetchStaffAvailability(selectedStaff._id, start, end);
			setAvailability(res.data || []);
		} catch (error) {
			console.error('Error fetching availability:', error);
			setAvailability([]);
		} finally {
			setLoadingAvailability(false);
		}
	};

	const getWeekDays = () => {
		const days = [];
		for (let i = 0; i < 7; i++) {
			days.push(moment(currentWeekStart).add(i, 'days'));
		}
		return days;
	};

	const getAvailabilityForDate = (date) => {
		const dateStr = date.format('YYYY-MM-DD');
		return availability.find(a => {
			const availDate = moment(a.date).format('YYYY-MM-DD');
			return availDate === dateStr;
		});
	};

	const handlePrevWeek = () => {
		setCurrentWeekStart(moment(currentWeekStart).subtract(7, 'days'));
	};

	const handleNextWeek = () => {
		setCurrentWeekStart(moment(currentWeekStart).add(7, 'days'));
	};

	const handleJumpToDate = (dateStr) => {
		if (dateStr) {
			setCurrentWeekStart(moment(dateStr).startOf('isoWeek'));
		}
	};

	const handleEditDay = (date) => {
		const existing = getAvailabilityForDate(date);
		if (existing && existing.windows && existing.windows.length > 0) {
			setEditWindows(existing.windows.map(w => ({
				start: w.start || '09:00',
				end: w.end || '17:00'
			})));
		} else {
			setEditWindows([{ start: '09:00', end: '17:00' }]);
		}
		setEditingDay(date.format('YYYY-MM-DD'));
		setShowApplyToWeek(false);
		setExcludedDays([]);
	};

	const handleAddWindow = () => {
		setEditWindows([...editWindows, { start: '09:00', end: '17:00' }]);
	};

	const handleRemoveWindow = (index) => {
		if (editWindows.length > 1) {
			setEditWindows(editWindows.filter((_, i) => i !== index));
		}
	};

	const handleWindowChange = (index, field, value) => {
		const updated = [...editWindows];
		updated[index] = { ...updated[index], [field]: value };
		setEditWindows(updated);
	};

	const handleSaveAvailability = async () => {
		if (!editingDay || !selectedStaff) return;

		setSaving(true);
		try {
			await Data.setStaffAvailability(selectedStaff._id, editingDay, editWindows);
			await fetchAvailability();
			setEditingDay(null);
		} catch (error) {
			console.error('Error saving availability:', error);
			alert('Failed to save availability');
		} finally {
			setSaving(false);
		}
	};

	const handleRemoveAvailability = async (date) => {
		if (!selectedStaff) return;

		if (!window.confirm('Remove availability for this day?')) return;

		setSaving(true);
		try {
			await Data.setStaffAvailability(selectedStaff._id, date.format('YYYY-MM-DD'), []);
			await fetchAvailability();
		} catch (error) {
			console.error('Error removing availability:', error);
			alert('Failed to remove availability');
		} finally {
			setSaving(false);
		}
	};

	const toggleExcludedDay = (dateStr) => {
		setExcludedDays((prev) =>
			prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]
		);
	};

	const getExcludedDayNames = () => {
		return excludedDays.map(d => moment(d).format('ddd'));
	};

	const generateDatesForWeeks = () => {
		const dates = [];
		const excludedDayNames = getExcludedDayNames();
		let endMoment;

		if (repeatMode === 'this-week') {
			const weekDays = getWeekDays();
			weekDays.forEach(d => {
				const dateStr = d.format('YYYY-MM-DD');
				if (!excludedDays.includes(dateStr)) {
					dates.push(dateStr);
				}
			});
			return dates;
		}

		if (repeatMode === 'until-date' && repeatEndDate) {
			endMoment = moment(repeatEndDate);
		} else if (repeatMode === 'indefinitely') {
			endMoment = moment(currentWeekStart).add(52, 'weeks');
		} else {
			return dates;
		}

		let weekStart = moment(currentWeekStart);
		while (weekStart.isSameOrBefore(endMoment)) {
			for (let i = 0; i < 7; i++) {
				const day = moment(weekStart).add(i, 'days');
				if (day.isAfter(endMoment)) break;
				if (!excludedDayNames.includes(day.format('ddd'))) {
					dates.push(day.format('YYYY-MM-DD'));
				}
			}
			weekStart.add(7, 'days');
		}

		return dates;
	};

	const handleApplyToWeek = async () => {
		if (!selectedStaff) return;

		if (repeatMode === 'indefinitely') {
			if (!window.confirm('This will apply availability for 52 weeks. Continue?')) return;
		}

		setSaving(true);
		try {
			const dates = generateDatesForWeeks();
			if (dates.length === 0) {
				alert('No dates selected to apply.');
				setSaving(false);
				return;
			}
			await Data.setBulkStaffAvailability(selectedStaff._id, dates, editWindows);
			await fetchAvailability();
			setEditingDay(null);
			setShowApplyToWeek(false);
			setExcludedDays([]);
			setRepeatMode('this-week');
			setRepeatEndDate('');
		} catch (error) {
			console.error('Error applying to week:', error);
			alert('Failed to apply availability');
		} finally {
			setSaving(false);
		}
	};

	if (loading) return <Loading button={false} />;

	if (staff.length === 0) {
		return (
			<div className={styles.availability}>
				<p>No staff members found. Please add staff members first.</p>
			</div>
		);
	}

	return (
		<div className={styles.availability}>
			{/* Staff Selector */}
			<div style={{ marginBottom: '20px' }}>
				<p style={{ marginBottom: '8px', opacity: 0.6 }}>Select Staff Member</p>
				<select
					value={selectedStaff?._id || ''}
					onChange={(e) => {
						const found = staff.find(s => s._id === e.target.value);
						setSelectedStaff(found);
					}}
					style={{ minWidth: '250px' }}
				>
					{staff.map(s => (
						<option key={s._id} value={s._id}>
							{s.firstName} {s.lastName}
						</option>
					))}
				</select>
			</div>

			{/* Week Navigation */}
			<div className={styles.weekNav}>
				<button className="btn btnEmpty" onClick={handlePrevWeek}>
					← Previous Week
				</button>
				<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
					<h3 style={{ margin: 0 }}>
						{currentWeekStart.format('MMM D')} - {moment(currentWeekStart).add(6, 'days').format('MMM D, YYYY')}
					</h3>
					<DatePicker
						value={parseISO(currentWeekStart.format('YYYY-MM-DD'))}
						onChange={(date) => handleJumpToDate(date ? format(date, 'yyyy-MM-dd') : '')}
						slotProps={{ textField: { size: 'small', style: { fontSize: '12px' } } }}
					/>
				</div>
				<button className="btn btnEmpty" onClick={handleNextWeek}>
					Next Week →
				</button>
			</div>

			{/* Week Calendar */}
			{loadingAvailability ? (
				<Loading button={false} />
			) : (
				<div className={styles.weekGrid}>
					{getWeekDays().map(day => {
						const dayAvail = getAvailabilityForDate(day);
						const hasAvailability = dayAvail && dayAvail.windows && dayAvail.windows.length > 0;
						const isEditing = editingDay === day.format('YYYY-MM-DD');
						const isPast = day.isBefore(moment(), 'day');

						return (
							<div
								key={day.format('YYYY-MM-DD')}
								className={`${styles.dayCard} ${isPast ? styles.past : ''}`}
							>
								<div className={styles.dayHeader}>
									<div className={styles.dayName}>{day.format('ddd')}</div>
									<div className={styles.dayNum}>{day.format('D')}</div>
								</div>

								{isEditing ? (
									<div>
										{editWindows.map((win, idx) => (
											<div key={idx} style={{ marginBottom: '6px', display: 'flex', gap: '4px', alignItems: 'center' }}>
												<TimePicker
													value={win.start ? parse(win.start, 'HH:mm', new Date()) : null}
													onChange={(date) => handleWindowChange(idx, 'start', date ? format(date, 'HH:mm') : '')}
													ampm={false}
													slotProps={{ textField: { size: 'small', style: { flex: 1, minWidth: 0 } } }}
												/>
												<span style={{ fontSize: '11px', color: '#888' }}>to</span>
												<TimePicker
													value={win.end ? parse(win.end, 'HH:mm', new Date()) : null}
													onChange={(date) => handleWindowChange(idx, 'end', date ? format(date, 'HH:mm') : '')}
													ampm={false}
													slotProps={{ textField: { size: 'small', style: { flex: 1, minWidth: 0 } } }}
												/>
												{editWindows.length > 1 && (
													<button
														onClick={() => handleRemoveWindow(idx)}
														style={{
															padding: '4px 8px',
															background: '#ffebee',
															color: '#c62828',
															border: 'none',
															borderRadius: '4px',
															cursor: 'pointer',
															fontSize: '14px'
														}}
													>
														×
													</button>
												)}
											</div>
										))}
										<button
											onClick={handleAddWindow}
											className="btn btnEmpty"
											style={{ width: '100%', padding: '6px', fontSize: '11px', marginBottom: '8px', minHeight: 'auto' }}
										>
											+ Add Slot
										</button>
										<div className={styles.dayActions}>
											<button
												onClick={handleSaveAvailability}
												disabled={saving}
												className="btn btnPrimary"
												style={{ flex: 1, padding: '8px', fontSize: '12px', minHeight: 'auto' }}
											>
												Save
											</button>
											<button
												onClick={() => setEditingDay(null)}
												className="btn btnEmpty"
												style={{ flex: 1, padding: '8px', fontSize: '12px', minHeight: 'auto' }}
											>
												Cancel
											</button>
										</div>
										<button
											onClick={() => { setShowApplyToWeek(true); setExcludedDays([]); }}
											disabled={saving}
											style={{
												width: '100%',
												marginTop: '8px',
												padding: '8px',
												background: '#4CAF50',
												color: 'white',
												border: 'none',
												borderRadius: '4px',
												cursor: 'pointer',
												fontSize: '11px'
											}}
										>
											Apply to Week...
										</button>
									</div>
								) : hasAvailability ? (
									<div>
										{dayAvail.windows.map((window, idx) => (
											<div key={idx} className={styles.timeSlot}>
												{window.start} - {window.end}
											</div>
										))}
										{!isPast && (
											<div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
												<button
													onClick={() => handleEditDay(day)}
													className="btn btnEmpty"
													style={{ width: '100%', padding: '6px', fontSize: '11px', minHeight: 'auto' }}
												>
													Edit
												</button>
												<button
													onClick={() => handleRemoveAvailability(day)}
													className="btn btnDanger"
													style={{ width: '100%', padding: '6px', fontSize: '11px', minHeight: 'auto' }}
												>
													Clear
												</button>
											</div>
										)}
									</div>
								) : (
									<div style={{ textAlign: 'center' }}>
										<div className={styles.noHours}>No hours set</div>
										{!isPast && (
											<button
												onClick={() => handleEditDay(day)}
												className="btn btnPrimary"
												style={{ padding: '8px 16px', fontSize: '12px', minHeight: 'auto' }}
											>
												+ Set Hours
											</button>
										)}
									</div>
								)}
							</div>
						);
					})}
				</div>
			)}

			{showApplyToWeek && editingDay && (
				<div style={{ marginTop: '16px', border: '1px solid #ccc', borderRadius: '6px', padding: '16px', background: '#fafafa' }}>
					<p style={{ fontSize: '14px', marginBottom: '10px', fontWeight: 'bold' }}>Apply to week</p>

					<div style={{ marginBottom: '12px' }}>
						<p style={{ fontSize: '13px', marginBottom: '6px' }}>Repeat:</p>
						<div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
							{[
								{ value: 'this-week', label: 'This week only' },
								{ value: 'until-date', label: 'Until date' },
								{ value: 'indefinitely', label: 'Indefinitely (52 weeks)' }
							].map(opt => (
								<label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', cursor: 'pointer' }}>
									<input
										type="radio"
										name="repeatMode"
										value={opt.value}
										checked={repeatMode === opt.value}
										onChange={() => setRepeatMode(opt.value)}
										style={{ margin: 0 }}
									/>
									{opt.label}
								</label>
							))}
						</div>
						{repeatMode === 'until-date' && (
							<DatePicker
								value={repeatEndDate ? parseISO(repeatEndDate) : null}
								onChange={(date) => setRepeatEndDate(date ? format(date, 'yyyy-MM-dd') : '')}
								minDate={parseISO(currentWeekStart.format('YYYY-MM-DD'))}
								slotProps={{ textField: { size: 'small', style: { marginTop: '8px' } } }}
							/>
						)}
					</div>

					<div style={{ marginBottom: '12px' }}>
						<p style={{ fontSize: '13px', marginBottom: '6px' }}>Exclude days:</p>
						<div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
							{getWeekDays().map(d => {
								const dateStr = d.format('YYYY-MM-DD');
								return (
									<label key={dateStr} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
										<input
											type="checkbox"
											checked={excludedDays.includes(dateStr)}
											onChange={() => toggleExcludedDay(dateStr)}
										/>
										{d.format('ddd')}
									</label>
								);
							})}
						</div>
					</div>

					<div style={{ display: 'flex', gap: '8px' }}>
						<button
							onClick={handleApplyToWeek}
							disabled={saving || (repeatMode === 'until-date' && !repeatEndDate)}
							style={{
								padding: '8px 20px',
								background: '#4CAF50',
								color: 'white',
								border: 'none',
								borderRadius: '4px',
								cursor: 'pointer',
								fontSize: '13px'
							}}
						>
							{saving ? 'Applying...' : 'Apply'}
						</button>
						<button
							onClick={() => { setShowApplyToWeek(false); setExcludedDays([]); setRepeatMode('this-week'); setRepeatEndDate(''); }}
							style={{
								padding: '8px 20px',
								background: '#eee',
								border: 'none',
								borderRadius: '4px',
								cursor: 'pointer',
								fontSize: '13px'
							}}
						>
							Cancel
						</button>
					</div>
				</div>
			)}
		</div>
	);
}

export default Manage;
